import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Instagram, Heart, MessageCircle, ExternalLink } from 'lucide-react';
import { ImageWithFallback } from './ImageWithFallback';
import './InstagramFeed.css';

// Mock Instagram posts - In production, this would fetch from Instagram API
// Note: Instagram API requires app approval and business verification
const MOCK_INSTAGRAM_POSTS = [
  {
    id: '1',
    imageUrl: 'https://images.unsplash.com/photo-1676300187485-8bd01a71d755?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXJnZXIlMjBjbG9zZSUyMHVwJTIwZ291cm1ldHxlbnwxfHx8fDE3NjQwOTk0NTl8MA&ixlib=rb-4.1.0&q=80&w=1080',
    username: '@foodie_nyc',
    likes: 342,
    comments: 23,
    caption: 'Best burger in NYC! 🍔 #RatedBiteBurger',
    dishTag: 'RatedBiteBurger',
    timestamp: '2h ago',
  },
  {
    id: '2',
    imageUrl: 'https://images.unsplash.com/photo-1672303185512-399a42b2cf4a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxyYW1lbiUyMGJvd2wlMjBhcnRpc3RpYyUyMHBob3RvfGVufDF8fHx8MTc2NDA5OTQ1OXww&ixlib=rb-4.1.0&q=80&w=1080',
    username: '@ramen_lover',
    likes: 521,
    comments: 45,
    caption: 'Perfection in a bowl 🍜 #RatedBiteRamen',
    dishTag: 'RatedBiteRamen',
    timestamp: '5h ago',
  },
  {
    id: '3',
    imageUrl: 'https://images.unsplash.com/photo-1667207394004-acb6aaf4790e?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwaXp6YSUyMG1hcmdoZXJpdGElMjBjbG9zZSUyMHVwfGVufDF8fHx8MTc2NDA5ODA5NHww&ixlib=rb-4.1.0&q=80&w=1080',
    username: '@pizza_adventures',
    likes: 892,
    comments: 67,
    caption: 'That perfect char 🔥 #RatedBitePizza',
    dishTag: 'RatedBitePizza',
    timestamp: '8h ago',
  },
  {
    id: '4',
    imageUrl: 'https://images.unsplash.com/photo-1705948731485-6e4c6c180d0d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXNoaSUyMHBsYXR0ZXIlMjBlbGVnYW50fGVufDF8fHx8MTc2NDAwNzMxMHww&ixlib=rb-4.1.0&q=80&w=1080',
    username: '@sushi_chronicles',
    likes: 1234,
    comments: 89,
    caption: 'Omakase excellence 🍣 #RatedBiteSushi',
    dishTag: 'RatedBiteSushi',
    timestamp: '12h ago',
  },
  {
    id: '5',
    imageUrl: 'https://images.unsplash.com/photo-1749169337822-d875fd6f4c9d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwYXN0YSUyMGl0YWxpYW4lMjBkaXNofGVufDF8fHx8MTc2Mzk4NzU1OHww&ixlib=rb-4.1.0&q=80&w=1080',
    username: '@pasta_perfection',
    likes: 678,
    comments: 34,
    caption: 'Handmade heaven 🍝 #RatedBitePasta',
    dishTag: 'RatedBitePasta',
    timestamp: '1d ago',
  },
  {
    id: '6',
    imageUrl: 'https://images.unsplash.com/photo-1736534632241-94e4aea9b16d?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxmb29kJTIwcGhvdG9ncmFwaHklMjBpbnN0YWdyYW0lMjBncmlkfGVufDF8fHx8MTc2NDA5OTQ1OHww&ixlib=rb-4.1.0&q=80&w=1080',
    username: '@dessert_dreams',
    likes: 445,
    comments: 28,
    caption: 'Sweet perfection 🍰 #RatedBiteDessert',
    dishTag: 'RatedBiteDessert',
    timestamp: '1d ago',
  },
];

function InstagramFeed({ hashtag = null, limit = 6 }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      let filteredPosts = MOCK_INSTAGRAM_POSTS;

      if (hashtag) {
        filteredPosts = MOCK_INSTAGRAM_POSTS.filter(post =>
          post.caption.toLowerCase().includes(hashtag.toLowerCase())
        );
      }

      setPosts(filteredPosts.slice(0, limit));
      setLoading(false);
    }, 500);
  }, [hashtag, limit]);

  if (loading) {
    return (
      <div className="border-t border-b border-border py-16 bg-stone-50">
        <div className="container mx-auto px-8 max-w-7xl">
          <div className="text-center">
            <p className="text-muted-foreground">Loading Instagram posts...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t border-b border-border py-16 bg-stone-50">
      <div className="container mx-auto px-8 max-w-7xl">
        <div className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Instagram className="w-6 h-6" />
                <p className="text-xs uppercase tracking-widest text-muted-foreground">
                  From the Community
                </p>
              </div>
              <h2 className="serif text-6xl mb-3">Latest Food Shots</h2>
              <p className="text-muted-foreground">
                Tag your photos with #RatedBite to appear here
              </p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-6 gap-4">
          {posts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              className="group relative"
            >
              <div className="aspect-square overflow-hidden bg-background border border-border">
                <ImageWithFallback
                  src={post.imageUrl}
                  alt={post.dishTag}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
              </div>

              {/* Hover Overlay */}
              <div className="absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white p-4">
                <div className="flex items-center gap-4 mb-3">
                  <div className="flex items-center gap-1.5">
                    <Heart className="w-4 h-4 fill-current" />
                    <span className="text-xs">{post.likes}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MessageCircle className="w-4 h-4" />
                    <span className="text-xs">{post.comments}</span>
                  </div>
                </div>
                <p className="text-xs text-center mb-2">{post.username.replace('@', '')}</p>
                <a
                  href={`https://instagram.com/${post.username.replace('@', '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs uppercase tracking-widest flex items-center gap-1.5 hover:underline"
                >
                  <span>View on Instagram</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </motion.article>
          ))}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center border border-border bg-background p-8">
          <h3 className="serif text-3xl mb-3">Share Your Food Photos</h3>
          <p className="text-sm text-muted-foreground mb-6">
            Tag your Instagram posts with <strong>#RatedBite</strong> and your favorite dish hashtag
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {['#RatedBiteBurger', '#RatedBitePizza', '#RatedBiteSushi', '#RatedBiteRamen', '#RatedBitePasta'].map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 border border-border text-xs"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default InstagramFeed;
